import { Alert, Box } from '@mui/material';
import moment from 'moment/moment';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useLoggedInEmployeeId } from '~/components/Auth';
import { EnumSelect } from '~/components/GenericForm/EnumSelect';
import {
  AnalyticsReportDefaultProps,
  AnalyticsReportScope,
  AnalyticsReportTypes,
  useCompensationEqualityReportData,
} from '~/data/reports';
import {
  ReportDatasetLayout,
  ReportDatasetSelector,
  ReportDateRangeSelector,
  ReportDateRangeSelectorMode,
  ReportViewLayout,
  useReportDataset,
} from '~/scenes/PortalModules/Analytics/common';
import { ReportTabsLayout } from '~/scenes/PortalModules/Analytics/common/ReportTabsLayout';
import { CompensationEqualityTabs } from '~/scenes/PortalModules/Analytics/Rewarding/CompensationEqualityReport/CompensationEqualityTabs/component';
import { useCompensationEqualityReport } from '~/scenes/PortalModules/Analytics/Rewarding/CompensationEqualityReport/useCompensationEqualityReport';

enum CompensationEqualityReportTab {
  Charts = 'charts',
  Table = 'table',
}

export const CompensationEqualityReport = () => {
  const { t } = useTranslation();
  const loggedInEmployeeId = useLoggedInEmployeeId();

  const [reportTab, setReportTab] = useState<CompensationEqualityReportTab>(
    CompensationEqualityReportTab.Charts,
  );
  const tabLabels = useMemo((): {
    [key in CompensationEqualityReportTab]: string;
  } => {
    return {
      [CompensationEqualityReportTab.Charts]: t('charts'),
      [CompensationEqualityReportTab.Table]: t('laTable'),
    };
  }, [t]);

  const defaultReportSettings: AnalyticsReportDefaultProps<object> = {
    reportType: {
      type: AnalyticsReportTypes.HR,
      employeeId: loggedInEmployeeId,
      scope: AnalyticsReportScope.Direct,
    },
    params: {
      startDate: moment().toDate(),
      endDate: moment().toDate(),
    },
  };

  const reportDatasetProps = useReportDataset({
    selection: defaultReportSettings,
    config: {
      options: {
        types: [
          // Company option disabled until we have clarity on user access conditions
          // AnalyticsReportTypes.Company,
          AnalyticsReportTypes.HR,
          AnalyticsReportTypes.Unit,
        ],
      },
    },
  });
  const { reportSelection, reportEnabled } = reportDatasetProps;

  const { data, isLoading, failedToLoad } =
    useCompensationEqualityReportData(reportSelection);

  const { rows, positionFilter, currencyFilter } =
    useCompensationEqualityReport({
      data,
    });

  return (
    <Box>
      <ReportViewLayout
        heading={t('laCompensationEqualityReport')}
        slots={{
          toolbar: {
            main: (
              <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <ReportDatasetLayout showHeading={true}>
                  <ReportDatasetSelector
                    {...reportDatasetProps}
                    isLoading={isLoading}
                    failedToLoad={failedToLoad}
                    renderCurrentParams={({ params }) => {
                      return <>{`${moment(params?.startDate).format('L')}`}</>;
                    }}
                    renderParamsFields={({ params, onChange }) => {
                      return (
                        <>
                          <ReportDateRangeSelector
                            startDate={params?.startDate}
                            endDate={params?.endDate}
                            onRangeChange={(startDate, endDate) =>
                              onChange({ ...params, startDate, endDate })
                            }
                            minDate={moment()
                              .startOf('year')
                              .subtract(9, 'years')
                              .toDate()}
                            maxDate={moment().endOf('year').toDate()}
                            mode={ReportDateRangeSelectorMode.SingleDateRange}
                          />
                        </>
                      );
                    }}
                  />
                </ReportDatasetLayout>
                {positionFilter.options.length > 0 && (
                  <EnumSelect
                    enumOptions={positionFilter.options}
                    value={positionFilter.value}
                    label={t('laPosition')}
                    onChange={(event) => {
                      const value = event.target.value;
                      if (!!value && typeof value === 'string') {
                        positionFilter.setValue(value);
                      }
                    }}
                    size={'small'}
                    disabled={positionFilter.options.length === 1}
                  />
                )}
                {currencyFilter.options.length > 0 && (
                  <EnumSelect
                    enumOptions={currencyFilter.options}
                    getOptionLabel={(option) =>
                      `${option.code} - ${option.name}`
                    }
                    value={currencyFilter.value}
                    label={t('laCurrency')}
                    onChange={(event) => {
                      const value = event.target.value;
                      if (!!value && typeof value === 'string') {
                        currencyFilter.setValue(value);
                      }
                    }}
                    size={'small'}
                    disabled={currencyFilter.options.length === 1}
                  />
                )}
              </Box>
            ),
          },
        }}
      />

      {reportEnabled ? (
        <ReportTabsLayout<CompensationEqualityReportTab>
          reportTab={reportTab}
          setReportTab={setReportTab}
          tabLabels={tabLabels}
        >
          <CompensationEqualityTabs
            reportTab={reportTab}
            reportSelection={reportSelection}
            rows={rows}
            currency={currencyFilter.value}
            isLoading={isLoading}
          />
        </ReportTabsLayout>
      ) : (
        <>
          <Alert severity={'error'}>{t('laGeneralError')}</Alert>
        </>
      )}
    </Box>
  );
};
